import { useCallback, useEffect } from 'react';
import { mutate } from 'swr';
import { useShallow } from 'zustand/react/shallow';
import Divider from '@components/_common/divider/Divider';
import PullToRefresh from '@components/_common/pull-to-refresh/PullToRefresh';
import CheckInPostStories from '@components/check-in-posts/CheckInPostStories';
import { FLOATING_BUTTON_SIZE } from '@components/header/floating-button/FloatingButton.styled';
import NoteSection from '@components/note/note-section/NoteSection';
import AllPostSection from '@components/post/AllPostSection';
import Profile from '@components/profile/Profile';
import { Layout } from '@design-system';
import { useRestoreScrollPosition } from '@hooks/useRestoreScrollPosition';
import { useBoundStore } from '@stores/useBoundStore';
import { UserSelector } from '@stores/user';
import { getMe, getMyProfile } from '@utils/apis/my';
import { logOnboardingEvent } from '@utils/apis/onboardingEvents';
import { userListApiPrefixForViewer } from '@utils/apis/userApiPrefix';
import { MainScrollContainer } from './Root';

function My() {
  const { myProfile, fetchCheckIn } = useBoundStore(
    useShallow((state) => ({
      myProfile: state.myProfile,
      fetchCheckIn: state.fetchCheckIn,
    })),
  );

  const { featureFlags } = useBoundStore(useShallow(UserSelector));

  // Refresh profile on mount
  useEffect(() => {
    getMyProfile();
    logOnboardingEvent('my_tab_opened');
  }, []);

  const handleRefresh = useCallback(async () => {
    const p = userListApiPrefixForViewer(featureFlags?.postsVerQ, myProfile?.current_ver);
    if (featureFlags?.questionResponseFeature) {
      await Promise.all([
        mutate(`${p}user/me/all-posts/`),
        fetchCheckIn(),
        getMe(),
        getMyProfile(),
      ]);
    } else {
      await Promise.all([mutate(`${p}user/me/notes/`), fetchCheckIn(), getMe(), getMyProfile()]);
    }
  }, [
    featureFlags?.questionResponseFeature,
    featureFlags?.postsVerQ,
    myProfile?.current_ver,
    fetchCheckIn,
  ]);

  const { scrollRef } = useRestoreScrollPosition('myPage');

  return (
    <MainScrollContainer scrollRef={scrollRef}>
      <PullToRefresh onRefresh={handleRefresh}>
        <Layout.FlexCol w="100%" pb={FLOATING_BUTTON_SIZE + 20}>
          <Divider width={8} bgColor="LIGHT" />
          <Layout.FlexRow
            w="100%"
            alignItems="center"
            justifyContent="space-between"
            p={12}
            bgColor="WHITE"
            rounded={8}
          >
            <Profile user={myProfile} />
          </Layout.FlexRow>
          {featureFlags?.checkInPosts && myProfile?.id && (
            <CheckInPostStories authorUserId={myProfile.id} isOwnProfile showCompose />
          )}
          <Divider width={8} bgColor="LIGHT" />
          <Layout.FlexCol pl={12} pb="default" w="100%" bgColor="WHITE" rounded="0px 0px 8px 8px">
            {featureFlags?.questionResponseFeature ? <AllPostSection /> : <NoteSection />}
          </Layout.FlexCol>
        </Layout.FlexCol>
      </PullToRefresh>
    </MainScrollContainer>
  );
}

export default My;
